import {
  Alert,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Container,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  List,
  ListItem,
  ListItemProps,
  Spinner,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import { ChangeEvent, KeyboardEvent, ReactElement, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Header from "../../components/header";
import axios from "../../config/axios";
import { DefaultErrorAttributes } from "../../types";
import {
  alertAnimationProps,
  BookIcon,
  CalendarIcon,
  CLOSE_ICON_PATH,
  dropdownAnimationProps,
  EuroIcon,
  formFieldProps,
  isClickOutside,
  MotionBox,
  MotionFlex,
  scrollToHighlighted,
} from "./ebook-setup-page.config";
import {
  badgeStyles,
  cardStyles,
  cardVariants,
  clearButtonStyles,
  disabledButtonStyles,
  dropdownItemStyles,
  dropdownStyles,
  footerStyles,
  getIconContainerStyles,
  iconWrapperStyles,
  inputStyles,
  labelStyles,
  shimmerBarStyles,
} from "./styles";
import { Author, EbookCreateDto, IEbookNumberInSeriesDto, ISeriesDto, mapToPriceOptions, PriceOption } from "./types";

type InputFocusHandlers = {
  onFocus: () => void;
  onBlur: () => void;
};

type AxiosErrorResponse = {
  response?: { data?: DefaultErrorAttributes };
};

const EbookSetupPage = (): ReactElement => {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [priceEUR, setPriceEUR] = useState("");
  const [success, setSuccess] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [authorSearch, setAuthorSearch] = useState("");
  const [authors, setAuthors] = useState<Author[]>([]);
  const [publishingDate, setPublishingDate] = useState("");
  const [isLoadingAuthors, setIsLoadingAuthors] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [authorId, setAuthorId] = useState<number | null>(null);
  const [isAuthorDropdownOpen, setIsAuthorDropdownOpen] = useState(false);
  const [selectedAuthor, setSelectedAuthor] = useState<Author | null>(null);
  const [highlightedAuthorIndex, setHighlightedAuthorIndex] = useState(-1);
  const authorInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [priceOptions, setPriceOptions] = useState<PriceOption[]>([]);
  const [priceSearch, setPriceSearch] = useState("");
  const [isPriceDropdownOpen, setIsPriceDropdownOpen] = useState(false);
  const [selectedPrice, setSelectedPrice] = useState<PriceOption | null>(null);
  const [filteredPrices, setFilteredPrices] = useState<PriceOption[]>([]);
  const [highlightedPriceIndex, setHighlightedPriceIndex] = useState(-1);
  const priceInputRef = useRef<HTMLInputElement>(null);
  const priceDropdownRef = useRef<HTMLDivElement>(null);

  const [seriesSearch, setSeriesSearch] = useState("");
  const [seriesOptions, setISeriesDtos] = useState<ISeriesDto[]>([]);
  const [isSeriesDropdownOpen, setIsSeriesDropdownOpen] = useState(false);
  const [selectedSeries, setSelectedSeries] = useState<ISeriesDto | null>(null);
  const [seriesId, setSeriesId] = useState<number | null>(null);
  const [isLoadingSeries, setIsLoadingSeries] = useState(false);
  const [highlightedSeriesIndex, setHighlightedSeriesIndex] = useState(-1);
  const [numberInSeries, setNumberInSeries] = useState<number | null>(null);
  const seriesInputRef = useRef<HTMLInputElement>(null);
  const seriesDropdownRef = useRef<HTMLDivElement>(null);

  const [longDescription, setLongDescription] = useState("");
  const [hasPrintBook, setHasPrintBook] = useState(false);
  const inputFocusProps: InputFocusHandlers = {
    onFocus: (): void => setIsTyping(true),
    onBlur: (): void => setIsTyping(false),
  };

  const fetchAuthors = useCallback(async (query: string): Promise<void> => {
    setIsLoadingAuthors(true);
    try {
      const response = await axios.get<Author[]>("/api/author", {
        params: { query: query || undefined },
      });
      setAuthors(response.data);
    } catch (err) {
      console.error("Failed to fetch authors:", err);
      setAuthors([]);
    } finally {
      setIsLoadingAuthors(false);
    }
  }, []);

  useEffect((): (() => void) => {
    const timeoutId = setTimeout((): void => {
      if (isAuthorDropdownOpen) fetchAuthors(authorSearch);
    }, 300);
    return (): void => clearTimeout(timeoutId);
  }, [authorSearch, isAuthorDropdownOpen, fetchAuthors]);

  const fetchSeries = useCallback(async (query: string): Promise<void> => {
    if (!query.trim()) {
      setISeriesDtos([]);
      return;
    }
    setIsLoadingSeries(true);
    try {
      const response = await axios.get<ISeriesDto[]>("/api/series/search", {
        params: { query },
      });
      setISeriesDtos(response.data);
    } catch (err) {
      console.error("Failed to fetch series:", err);
      setISeriesDtos([]);
    } finally {
      setIsLoadingSeries(false);
    }
  }, []);

  useEffect((): (() => void) => {
    const timeoutId = setTimeout((): void => {
      if (isSeriesDropdownOpen) fetchSeries(seriesSearch);
    }, 300);
    return (): void => clearTimeout(timeoutId);
  }, [seriesSearch, isSeriesDropdownOpen, fetchSeries]);

  useEffect((): void => {
    axios.get<number[]>("/api/prices").then((response): void => {
      const options: PriceOption[] = mapToPriceOptions(response.data);
      setPriceOptions(options);
      setFilteredPrices(options);
    }).catch((err: unknown): void => {
      console.error("Failed to fetch prices:", err);
    });
  }, []);

  useEffect((): (() => void) => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (isClickOutside(event, dropdownRef, authorInputRef)) setIsAuthorDropdownOpen(false);
      if (isClickOutside(event, priceDropdownRef, priceInputRef)) setIsPriceDropdownOpen(false);
      if (isClickOutside(event, seriesDropdownRef, seriesInputRef)) setIsSeriesDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return (): void => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleAuthorSelect = (author: Author): void => {
    setSelectedAuthor(author);
    setAuthorId(author.id);
    setAuthorSearch(`${author.firstName} ${author.lastName}`);
    setIsAuthorDropdownOpen(false);
  };

  const handleAuthorInputChange = (value: string): void => {
    setAuthorSearch(value);
    setSelectedAuthor(null);
    setAuthorId(null);
    if (!isAuthorDropdownOpen) setIsAuthorDropdownOpen(true);
  };

  const handleClearAuthor = (): void => {
    setSelectedAuthor(null);
    setAuthorId(null);
    setAuthorSearch("");
    setHighlightedAuthorIndex(-1);
    authorInputRef.current?.focus();
  };

  const handleAuthorKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (!isAuthorDropdownOpen) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedAuthorIndex((prev: number): number => prev < authors.length - 1 ? prev + 1 : prev);
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedAuthorIndex((prev: number): number => prev > 0 ? prev - 1 : prev);
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedAuthorIndex >= 0 && authors[highlightedAuthorIndex]) handleAuthorSelect(authors[highlightedAuthorIndex]);
        break;
      case "Escape":
        setIsAuthorDropdownOpen(false);
        setHighlightedAuthorIndex(-1);
        break;
    }
  };

  useEffect((): void => { setHighlightedAuthorIndex(-1); }, [authors]);
  useEffect((): void => { scrollToHighlighted(highlightedAuthorIndex, dropdownRef); }, [highlightedAuthorIndex]);

  useEffect((): void => {
    const search: string = priceSearch.replace(",", ".").toLowerCase();
    const filtered: PriceOption[] = priceOptions.filter(
      (p: PriceOption): boolean => p.value.includes(search) || p.label.toLowerCase().includes(search),
    );
    setFilteredPrices(filtered);
  }, [priceSearch, priceOptions]);

  const handlePriceSelect = (price: PriceOption): void => {
    setSelectedPrice(price);
    setPriceEUR(price.value);
    setPriceSearch(price.label);
    setIsPriceDropdownOpen(false);
  };

  const handlePriceInputChange = (value: string): void => {
    setPriceSearch(value);
    setSelectedPrice(null);
    setPriceEUR("");
    if (!isPriceDropdownOpen) setIsPriceDropdownOpen(true);
  };

  const handleClearPrice = (): void => {
    setSelectedPrice(null);
    setPriceEUR("");
    setPriceSearch("");
    setHighlightedPriceIndex(-1);
    priceInputRef.current?.focus();
  };

  const handlePriceKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (!isPriceDropdownOpen) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedPriceIndex((prev: number): number => prev < filteredPrices.length - 1 ? prev + 1 : prev);
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedPriceIndex((prev: number): number => prev > 0 ? prev - 1 : prev);
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedPriceIndex >= 0 && filteredPrices[highlightedPriceIndex]) handlePriceSelect(filteredPrices[highlightedPriceIndex]);
        break;
      case "Escape":
        setIsPriceDropdownOpen(false);
        setHighlightedPriceIndex(-1);
        break;
    }
  };

  useEffect((): void => { setHighlightedPriceIndex(-1); }, [filteredPrices]);
  useEffect((): void => { scrollToHighlighted(highlightedPriceIndex, priceDropdownRef); }, [highlightedPriceIndex]);

  const handleSeriesSelect = (series: ISeriesDto): void => {
    setSelectedSeries(series);
    setSeriesId(series.id);
    setSeriesSearch(series.title);
    setIsSeriesDropdownOpen(false);
    axios.get<IEbookNumberInSeriesDto[]>(`/api/ebook/series/${series.id}`)
      .then((response): void => {
        const max: number = response.data.reduce((acc: number, e: IEbookNumberInSeriesDto): number => Math.max(acc, e.numberInSeries ?? 0), 0);
        setNumberInSeries(max + 1);
      })
      .catch((): void => setNumberInSeries(1));
  };

  const handleSeriesInputChange = (value: string): void => {
    setSeriesSearch(value);
    setSelectedSeries(null);
    setSeriesId(null);
    if (!isSeriesDropdownOpen) setIsSeriesDropdownOpen(true);
  };

  const handleClearSeries = (): void => {
    setSelectedSeries(null);
    setSeriesId(null);
    setSeriesSearch("");
    setNumberInSeries(null);
    setHighlightedSeriesIndex(-1);
    seriesInputRef.current?.focus();
  };

  const handleSeriesKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (!isSeriesDropdownOpen) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedSeriesIndex((prev: number): number => prev < seriesOptions.length - 1 ? prev + 1 : prev);
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedSeriesIndex((prev: number): number => prev > 0 ? prev - 1 : prev);
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedSeriesIndex >= 0 && seriesOptions[highlightedSeriesIndex]) handleSeriesSelect(seriesOptions[highlightedSeriesIndex]);
        break;
      case "Escape":
        setIsSeriesDropdownOpen(false);
        setHighlightedSeriesIndex(-1);
        break;
    }
  };

  useEffect((): void => { setHighlightedSeriesIndex(-1); }, [seriesOptions]);
  useEffect((): void => { scrollToHighlighted(highlightedSeriesIndex, seriesDropdownRef); }, [highlightedSeriesIndex]);

  const onSubmit = async (): Promise<void> => {
    setErrorMessage(null);
    setSuccess(false);
    setIsLoading(true);

    const dto: EbookCreateDto = {
      title,
      publishingDate: new Date(publishingDate),
      priceEUR,
      authorId: authorId!,
      seriesId: seriesId || undefined,
      numberInSeries: numberInSeries || undefined,
      longDescription: longDescription || undefined,
      hasPrintBook,
    };

    try {
      await axios.post("/api/ebook", dto);
      setSuccess(true);
      setTitle("");
      setPublishingDate("");
      setAuthorId(null);
      setAuthorSearch("");
      setSelectedAuthor(null);
      setPriceEUR("");
      setPriceSearch("");
      setSelectedPrice(null);
      setSeriesId(null);
      setSeriesSearch("");
      setSelectedSeries(null);
      setNumberInSeries(null);
      setLongDescription("");
      setHasPrintBook(false);
    } catch (err: unknown) {
      console.error(err);
      const axiosError = err as AxiosErrorResponse;
      const message: string = axiosError.response?.data?.message || t("ebookCreateError");
      setErrorMessage(message);
    } finally {
      setIsLoading(false);
    }
  };

  const LONG_DESCRIPTION_MAX_LENGTH = 8000;
  const isLongDescriptionTooLong: boolean = longDescription.length > LONG_DESCRIPTION_MAX_LENGTH;
  const isFormValid: boolean = !!(title && publishingDate && priceEUR && authorId) && !isLongDescriptionTooLong;

  return (
    <Box minH="100vh" bg="gray.50">
      <Header />
      <Container maxW="container.md" py={8}>
        <MotionBox initial="hidden" animate="visible" variants={cardVariants}>
          <Box sx={cardStyles}>
            <Box sx={shimmerBarStyles} />
            <Box
              px={8}
              pt={8}
              pb={6}
              borderBottom="1px solid"
              borderColor="gray.100"
              bg="linear-gradient(180deg, rgba(49, 151, 149, 0.03) 0%, transparent 100%)"
            >
              <Flex align="center" gap={5}>
                <MotionBox
                  initial={{ scale: 0, rotate: -15 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 15 }}
                >
                  <Box
                    position="relative"
                    cursor={isFormValid ? "pointer" : "not-allowed"}
                    onClick={(): false | Promise<void> => isFormValid && !isLoading && onSubmit()}
                    sx={getIconContainerStyles(isTyping, isFormValid)}
                    title={isFormValid ? t("save") : undefined}
                  >
                    <Box sx={iconWrapperStyles}>
                      <BookIcon />
                    </Box>
                    <Box sx={badgeStyles}>
                      <Text fontSize="xs" fontWeight="bold" color="white" lineHeight="1">+</Text>
                    </Box>
                  </Box>
                </MotionBox>
                <Box flex="1">
                  <Heading size="lg" color="gray.800" fontWeight="700" letterSpacing="-0.02em">
                    {t("ebookSetup")}
                  </Heading>
                  <Text color="gray.400" fontSize="sm" mt={1} fontWeight="400">
                    {t("createEbook")}
                  </Text>
                </Box>
              </Flex>
            </Box>

            <Box p={8}>
              <VStack spacing={5} align="stretch">
                <MotionBox {...formFieldProps(0)}>
                  <FormControl isRequired>
                    <FormLabel sx={labelStyles}>{t("title")}</FormLabel>
                    <Input
                      placeholder={t("title")}
                      value={title}
                      maxLength={255}
                      onChange={({ target }: ChangeEvent<HTMLInputElement>): void => setTitle(target.value)}
                      sx={inputStyles}
                      {...inputFocusProps}
                    />
                  </FormControl>
                </MotionBox>

                <MotionBox {...formFieldProps(1)}>
                  <FormControl isRequired>
                    <FormLabel sx={labelStyles}>{t("publishingDate")}</FormLabel>
                    <InputGroup>
                      <InputLeftElement pointerEvents="none" h="full" pl={3}>
                        <CalendarIcon />
                      </InputLeftElement>
                      <Input
                        type="date"
                        value={publishingDate}
                        onChange={({ target }: ChangeEvent<HTMLInputElement>): void => setPublishingDate(target.value)}
                        sx={{ ...inputStyles, pl: 10 }}
                        {...inputFocusProps}
                      />
                    </InputGroup>
                  </FormControl>
                </MotionBox>

                <MotionBox {...formFieldProps(2)}>
                  <FormControl isRequired>
                    <FormLabel sx={labelStyles}>{t("author")}</FormLabel>
                    <Box position="relative">
                      <InputGroup>
                        <Input
                          ref={authorInputRef}
                          value={authorSearch}
                          onChange={({ target }: ChangeEvent<HTMLInputElement>): void => handleAuthorInputChange(target.value)}
                          onFocus={(): void => {
                            setIsTyping(true);
                            setIsAuthorDropdownOpen(true);
                            if (authors.length === 0) fetchAuthors(authorSearch);
                          }}
                          onBlur={(): void => setIsTyping(false)}
                          onKeyDown={handleAuthorKeyDown}
                          placeholder={t("searchAuthor")}
                          sx={{ ...inputStyles, pr: selectedAuthor ? 10 : 4 }}
                          autoComplete="off"
                        />
                        {isLoadingAuthors && (
                          <InputRightElement h="full" pr={3}>
                            <Spinner size="sm" color="teal.500" />
                          </InputRightElement>
                        )}
                        {selectedAuthor && !isLoadingAuthors && (
                          <InputRightElement h="full" pr={3}>
                            <Box as="button" type="button" onClick={handleClearAuthor} sx={clearButtonStyles}>
                              <Icon viewBox="0 0 24 24" boxSize={4}>
                                <path fill="currentColor" d={CLOSE_ICON_PATH} />
                              </Icon>
                            </Box>
                          </InputRightElement>
                        )}
                      </InputGroup>
                      <AnimatePresence>
                        {isAuthorDropdownOpen && (
                          <MotionBox ref={dropdownRef} {...dropdownAnimationProps} sx={dropdownStyles}>
                            <List spacing={0}>
                              {authors.length === 0 && !isLoadingAuthors && (
                                <ListItem px={4} py={3} color="gray.500" fontSize="sm">
                                  {t("noAuthorsFound")}
                                </ListItem>
                              )}
                              {authors.map((author: Author, index: number): ReactElement<ListItemProps, typeof ListItem> => (
                                <ListItem
                                  key={author.id}
                                  sx={dropdownItemStyles}
                                  bg={highlightedAuthorIndex === index ? "teal.50" : "transparent"}
                                  onClick={(): void => handleAuthorSelect(author)}
                                  onMouseEnter={(): void => setHighlightedAuthorIndex(index)}
                                >
                                  <Text fontWeight="500" color="gray.800">
                                    {author.firstName} {author.lastName}
                                  </Text>
                                </ListItem>
                              ))}
                            </List>
                          </MotionBox>
                        )}
                      </AnimatePresence>
                    </Box>
                  </FormControl>
                </MotionBox>

                <MotionBox {...formFieldProps(3)}>
                  <FormControl isRequired>
                    <FormLabel sx={labelStyles}>{t("priceEur")}</FormLabel>
                    <Box position="relative">
                      <InputGroup>
                        <InputLeftElement pointerEvents="none" h="full" pl={3}>
                          <EuroIcon />
                        </InputLeftElement>
                        <Input
                          ref={priceInputRef}
                          value={priceSearch}
                          onChange={({ target }: ChangeEvent<HTMLInputElement>): void => handlePriceInputChange(target.value)}
                          onFocus={(): void => {
                            setIsTyping(true);
                            setIsPriceDropdownOpen(true);
                          }}
                          onBlur={(): void => setIsTyping(false)}
                          onKeyDown={handlePriceKeyDown}
                          placeholder={t("selectPrice")}
                          sx={{ ...inputStyles, pl: 10, pr: selectedPrice ? 10 : 4 }}
                          autoComplete="off"
                        />
                        {selectedPrice && (
                          <InputRightElement h="full" pr={3}>
                            <Box as="button" type="button" onClick={handleClearPrice} sx={clearButtonStyles}>
                              <Icon viewBox="0 0 24 24" boxSize={4}>
                                <path fill="currentColor" d={CLOSE_ICON_PATH} />
                              </Icon>
                            </Box>
                          </InputRightElement>
                        )}
                      </InputGroup>
                      <AnimatePresence>
                        {isPriceDropdownOpen && (
                          <MotionBox ref={priceDropdownRef} {...dropdownAnimationProps} sx={dropdownStyles}>
                            <List spacing={0}>
                              {filteredPrices.length === 0 && (
                                <ListItem px={4} py={3} color="gray.500" fontSize="sm">
                                  {t("noPricesFound")}
                                </ListItem>
                              )}
                              {filteredPrices.map((price: PriceOption, index: number): ReactElement<ListItemProps, typeof ListItem> => (
                                <ListItem
                                  key={price.id}
                                  sx={dropdownItemStyles}
                                  bg={highlightedPriceIndex === index ? "teal.50" : "transparent"}
                                  onClick={(): void => handlePriceSelect(price)}
                                  onMouseEnter={(): void => setHighlightedPriceIndex(index)}
                                >
                                  <Text fontWeight="500" color="gray.800">
                                    {price.label}
                                  </Text>
                                </ListItem>
                              ))}
                            </List>
                          </MotionBox>
                        )}
                      </AnimatePresence>
                    </Box>
                  </FormControl>
                </MotionBox>

                <MotionBox {...formFieldProps(4)}>
                  <FormControl>
                    <FormLabel sx={labelStyles}>{t("series")}</FormLabel>
                    <Box position="relative">
                      <InputGroup>
                        <Input
                          ref={seriesInputRef}
                          value={seriesSearch}
                          onChange={({ target }: ChangeEvent<HTMLInputElement>): void => handleSeriesInputChange(target.value)}
                          onFocus={(): void => {
                            setIsTyping(true);
                            if (seriesSearch.trim()) setIsSeriesDropdownOpen(true);
                          }}
                          onBlur={(): void => setIsTyping(false)}
                          onKeyDown={handleSeriesKeyDown}
                          placeholder={t("searchSeries")}
                          sx={{ ...inputStyles, pr: selectedSeries ? 10 : 4 }}
                          autoComplete="off"
                        />
                        {isLoadingSeries && (
                          <InputRightElement h="full" pr={3}>
                            <Spinner size="sm" color="teal.500" />
                          </InputRightElement>
                        )}
                        {selectedSeries && !isLoadingSeries && (
                          <InputRightElement h="full" pr={3}>
                            <Box as="button" type="button" onClick={handleClearSeries} sx={clearButtonStyles}>
                              <Icon viewBox="0 0 24 24" boxSize={4}>
                                <path fill="currentColor" d={CLOSE_ICON_PATH} />
                              </Icon>
                            </Box>
                          </InputRightElement>
                        )}
                      </InputGroup>
                      <AnimatePresence>
                        {isSeriesDropdownOpen && (
                          <MotionBox ref={seriesDropdownRef} {...dropdownAnimationProps} sx={dropdownStyles}>
                            <List spacing={0}>
                              {seriesOptions.length === 0 && !isLoadingSeries && (
                                <ListItem px={4} py={3} color="gray.500" fontSize="sm">
                                  {t("noSeriesFound")}
                                </ListItem>
                              )}
                              {seriesOptions.map((series: ISeriesDto, index: number): ReactElement<ListItemProps, typeof ListItem> => (
                                <ListItem
                                  key={series.id}
                                  sx={dropdownItemStyles}
                                  bg={highlightedSeriesIndex === index ? "teal.50" : "transparent"}
                                  onClick={(): void => handleSeriesSelect(series)}
                                  onMouseEnter={(): void => setHighlightedSeriesIndex(index)}
                                >
                                  <Text fontWeight="500" color="gray.800">
                                    {series.title}
                                  </Text>
                                  <Text fontSize="xs" color="gray.500">
                                    {series.code}
                                  </Text>
                                </ListItem>
                              ))}
                            </List>
                          </MotionBox>
                        )}
                      </AnimatePresence>
                    </Box>
                  </FormControl>
                </MotionBox>

                <MotionBox {...formFieldProps(5)}>
                  <FormControl isDisabled={!selectedSeries}>
                    <FormLabel sx={labelStyles}>{t("numberInSeries")}</FormLabel>
                    <Input
                      type="number"
                      value={numberInSeries ?? ""}
                      isReadOnly
                      sx={inputStyles}
                    />
                    {!selectedSeries && (
                      <Text fontSize="xs" color="gray.400" mt={1}>
                        {t("numberInSeriesHint")}
                      </Text>
                    )}
                  </FormControl>
                </MotionBox>

                <MotionBox {...formFieldProps(6)}>
                  <FormControl isInvalid={isLongDescriptionTooLong}>
                    <FormLabel sx={labelStyles}>{t("longDescription")}</FormLabel>
                    <Textarea
                      placeholder={t("longDescription")}
                      value={longDescription}
                      rows={6}
                      onChange={({ target }: ChangeEvent<HTMLTextAreaElement>): void => setLongDescription(target.value)}
                      sx={inputStyles}
                      {...inputFocusProps}
                    />
                    <Flex justify="space-between" mt={1}>
                      {isLongDescriptionTooLong && (
                        <Text fontSize="xs" color="red.500">
                          {t("longDescriptionTooLong")}
                        </Text>
                      )}
                      <Text
                        fontSize="xs"
                        color={isLongDescriptionTooLong ? "red.500" : "gray.400"}
                        ml="auto"
                      >
                        {longDescription.length} / {LONG_DESCRIPTION_MAX_LENGTH}
                      </Text>
                    </Flex>
                  </FormControl>
                </MotionBox>

                <MotionBox {...formFieldProps(7)}>
                  <FormControl>
                    <Checkbox
                      isChecked={hasPrintBook}
                      onChange={(e: ChangeEvent<HTMLInputElement>): void => setHasPrintBook(e.target.checked)}
                      colorScheme="teal"
                      size="lg"
                    >
                      <Text fontSize="sm" fontWeight="500" color="gray.700">
                        {t("hasPrintBook")}
                      </Text>
                    </Checkbox>
                  </FormControl>
                </MotionBox>

                <AnimatePresence>
                  {errorMessage && (
                    <MotionBox {...alertAnimationProps}>
                      <Alert status="error" borderRadius="lg">
                        <AlertIcon />
                        <AlertTitle>{errorMessage}</AlertTitle>
                      </Alert>
                    </MotionBox>
                  )}
                </AnimatePresence>

                <AnimatePresence>
                  {success && (
                    <MotionBox {...alertAnimationProps}>
                      <Alert status="success" borderRadius="lg">
                        <AlertIcon />
                        <AlertTitle>{t("ebookCreated")}</AlertTitle>
                      </Alert>
                    </MotionBox>
                  )}
                </AnimatePresence>
              </VStack>
            </Box>

            <Box sx={footerStyles}>
              <MotionFlex
                justify="flex-end"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                <Button
                  as={motion.button}
                  colorScheme="teal"
                  size="lg"
                  px={8}
                  onClick={onSubmit}
                  isLoading={isLoading}
                  isDisabled={!isFormValid}
                  whileHover={{ scale: isFormValid ? 1.02 : 1, transition: { duration: 0.2 } }}
                  whileTap={{ scale: isFormValid ? 0.98 : 1 }}
                  _disabled={disabledButtonStyles}
                >
                  {t("save")}
                </Button>
              </MotionFlex>
            </Box>
          </Box>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default EbookSetupPage;
